import React, { useMemo, useState } from 'react';
import { addWeeks, format } from 'date-fns';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import { FormField } from '../../FormField/FormField';
import { CheckboxGroup } from '../../CheckboxGroup/CheckboxGroup';
import { useClient } from '../../../hooks/useClient';
import { Reservation } from '../../../types/reservation';
import { ExternalUserShort } from '../../../types/externalUser';
import { getUserOptions } from '../../../utils/selectOptions';
import { getFirstAvailableDateToClone } from '../../../utils/reservationUtils';
import { MAX_TIMES_TO_CLONE, MIN_TIMES_TO_CLONE } from '../constants';

interface Props {
  visible: boolean;
  onHide: () => void;
  resource: Reservation | undefined;
  isCloneable: boolean | undefined;
  usersToShow: ExternalUserShort[];
  onCloned: () => void;
}

export const CloneReservationDialog = ({ visible, onHide, resource, isCloneable, usersToShow, onCloned }: Props) => {
  const { post } = useClient();
  const [timesToClone, setTimesToClone] = useState<number | null>(1);
  const [userIdsToClone, setUserIdsToClone] = useState<string[]>();
  const [isCloning, setIsCloning] = useState(false);
  const noUsersToCloneSelected = userIdsToClone && userIdsToClone.length === 0;

  const startDayOfWeek = resource?.localStartDate ? format(resource?.localStartDate, 'EEEE') : null;
  const startHour = resource?.localStartDate ? format(resource?.localStartDate, 'HH:mm') : null;
  const firstAvailableDateToClone = useMemo(
    () => (resource?.localStartDate && isCloneable ? getFirstAvailableDateToClone(resource) : null),
    [resource, isCloneable],
  );

  const lastClonedReservationDate = useMemo(
    () => (firstAvailableDateToClone && timesToClone ? addWeeks(firstAvailableDateToClone, timesToClone - 1) : null),
    [firstAvailableDateToClone, timesToClone],
  );

  const handleTimesToCloneChange = (e: InputNumberChangeEvent) => {
    const value = e.value;
    if (value !== null) {
      const finalValue = Math.min(Math.max(value, MIN_TIMES_TO_CLONE), MAX_TIMES_TO_CLONE);
      setTimesToClone(finalValue);
      return;
    }
    setTimesToClone(1);
  };

  const handleCloneUsersChange = (selectedValues: string[]) => {
    setUserIdsToClone(selectedValues);
  };

  const handleHideCloneDialog = () => {
    onHide();
    setTimesToClone(1);
    setUserIdsToClone(undefined);
  };

  // Función para manejar la clonación de la reserva
  const handleClone = async () => {
    if (!resource || !timesToClone || noUsersToCloneSelected) return;

    setIsCloning(true);

    const payload = {
      reservationId: resource.id,
      timesToClone,
      userIdsToClone,
    };

    try {
      await post('/reservations/clone', {
        body: payload,
        successMessage: {
          summary: 'Reserva clonada',
          detail: `La reserva ha sido clonada correctamente para las próximas ${timesToClone} semanas`,
        },
        errorMessages: {
          summary: 'Error al clonar la reserva',
          defaultDetail: 'No se ha podido clonar la reserva',
          EXTERNAL_USER_HAS_CONFLICTING_RESERVATIONS:
            'Un usuario tiene reservas de otro tipo que entran en conflicto con una de las reservas clonadas.',
          RESERVATION_FULL: 'Una de las reservas clonadas excede el límite de asistentes.',
          COUPON_NOT_ENOUGH_USES: 'El bono de la reserva original no tiene suficientes usos restantes.',
        },
        handlers: {
          defaultSuccess: () => {
            handleHideCloneDialog();
            onCloned();
          },
        },
      });
    } catch (error) {
      console.error('Error al clonar la reserva', error);
    }
    setIsCloning(false);
  };

  return (
    <Dialog
      header='Clonar reserva'
      visible={visible}
      onHide={handleHideCloneDialog}
      className='clone-dialog big-modal'
      draggable={false}
      resizable={false}
      dismissableMask
      footer={
        <div className='dialog-footer'>
          <Button
            label='Cancelar'
            icon={PrimeIcons.TIMES}
            onClick={handleHideCloneDialog}
            className='p-button-text'
            disabled={isCloning}
          />
          <Button
            label='Clonar reserva'
            icon={PrimeIcons.COPY}
            onClick={handleClone}
            className='p-button-primary'
            loading={isCloning}
            disabled={!timesToClone || noUsersToCloneSelected}
          />
        </div>
      }
    >
      <div className='clone-dialog-content'>
        <p className='dialog-message'>
          Se creará una copia de esta reserva cada semana, los {startDayOfWeek} a las {startHour}, empezando el{' '}
          {firstAvailableDateToClone ? format(firstAvailableDateToClone, "dd 'de' MMMM 'de' yyyy") : 'hoy'}.
          {lastClonedReservationDate && (
            <> La última reserva clonada será el {format(lastClonedReservationDate, "dd 'de' MMMM 'de' yyyy")}.</>
          )}
        </p>
        <FormField labelTitle='Número de semanas a clonar' elementId='times-to-clone' required>
          <InputNumber
            id='times-to-clone'
            value={timesToClone}
            onChange={handleTimesToCloneChange}
            showButtons
            min={MIN_TIMES_TO_CLONE}
            max={MAX_TIMES_TO_CLONE}
            disabled={isCloning}
            allowEmpty={false}
          />
        </FormField>
        <FormField labelTitle='Clientes que incluir'>
          <CheckboxGroup
            options={getUserOptions(usersToShow) ?? []}
            value={userIdsToClone ?? resource?.confirmedExternalUsers?.map((user) => user.id) ?? []}
            onChange={handleCloneUsersChange}
            disabled={isCloning}
            showSelectAll
          />
        </FormField>
      </div>
    </Dialog>
  );
};
